var XMLNode = require('../xml/XMLNode');

/**
 * The Font class represents a Font configuration within an Office
 * Open XML Spreadsheet.
 *
 * @param {Object} oData - Configuration object for this Font
 * @param {boolean} [oData.bold] - Boolean value indicating if the Font should be bold
 * @param {string} [oData.name] - The name of the font that gets used by this Font object i.E. Arial, Verdana, etc.
 * @param {number|String} [oData.size] - The font size that is been used
 * @param {number} [oData.family] - The font family reference {@see getFamily}
 * @constructor
 */
function Font(oData) {
	XMLNode.call(this, Font.metadata);

	if (oData && oData instanceof Object) {
		this.setName(oData.name)
			.setSize(oData.size)
			.setFamily(oData.family)
			.setBold(!!oData.bold);
	}
}

Font.prototype = Object.create(XMLNode.prototype);
Font.prototype.constructor = Font;

Font.DEFAULT_SIZE = 11;
Font.DEFAULT_NAME = 'Arial';

Font.metadata = {
	tag: 'font',
	properties: [
		{
			id: 'size',
			tag: 'sz',
			attributes: [
				{
					id: 'valueAttribute',
					name: 'val',
					value: Font.DEFAULT_SIZE
				}
			]
		},
		{
			id: 'color',
			tag: 'color',
			attributes: [
				{
					id: 'valueAttribute',
					name: 'theme',
					value: '1'
				}
			]
		},
		{
			id: 'name',
			tag: 'name',
			attributes: [
				{
					id: 'valueAttribute',
					name: 'val',
					value: Font.DEFAULT_NAME
				}
			]
		},
		{
			id: 'family',
			tag: 'family',
			attributes: [
				{
					id: 'valueAttribute',
					name: 'val',
					value: 2
				}
			]
		}
	]
};

/**
 * Checks if the current Font is equal to the given parameter.
 *
 * @param {Font} oFont - The Font to compare with
 * @return {boolean} - Returns true if both Font objects contain the same configuration.
 */
Font.prototype.equals = function(oFont) {
	if (!(oFont instanceof Font)) {
		return false;
	}

	return this.getName() == oFont.getName()
		&& this.getSize() == oFont.getSize()
		&& this.isBold() == oFont.isBold()
		&& this.getColor() == oFont.getColor()
		&& this.getFamily() == oFont.getFamily();
};

/**
 * Returns whether the Font will be displayed bold or not.
 *
 * @returns {boolean} - Returns true if the Font is bold
 */
Font.prototype.isBold = function() {
	return !!this.bold;
};

/**
 * Returns the size of this Font.
 *
 * @returns {number} - The size of the Font
 */
Font.prototype.getSize = function() {
	return this.size.valueAttribute.value;
};

/**
 * Returns the name of this Font. I.E. Arial, Calibri, etc.
 *
 * @returns {string} - The name of the Font
 */
Font.prototype.getName = function() {
	return this.name.valueAttribute.value;
};

/**
 * Returns the color reference of this Font.
 *
 * @returns {string} - The color as HEX string
 */
Font.prototype.getColor = function() {
	return this.color.valueAttribute.value;
};

/**
 * Returns the Font family reference of this Font. The reference is
 * defined within the Office Open XML specification.
 * 1 : Roman (Times New Roman, SAP-icons)
 * 2 : Swiss (Arial, Calibri, Segoe UI, Tahoma, Verdana)
 * 3 : Modern
 * 4 : Script
 * 5 : Decorative
 * 6-14 : Reserved for future use
 *
 * @returns {number} - The Font family ID
 */
Font.prototype.getFamily = function() {
	return this.family.valueAttribute.value;
};

/**
 * This method can be used to set the Font to bold or not.
 *
 * @param {boolean} bBold - A boolean value that indicates if the Font should be bold or not
 * @return {Font} - Returns the current Font object for chaining
 */
Font.prototype.setBold = function(bBold) {
	var boldProperty = 'bold';

	/* Early return if bold status is already correct */
	if (bBold === !!this[boldProperty]) {
		return this;
	}

	if (bBold) {
		this.addChild({
			id: boldProperty,
			tag: 'b'
		});
	} else {
		this.remove(boldProperty);
	}

	return this;
};

/**
 * This method is used to set the Font family reference for the used font.
 *
 * @see {@link getFamily}
 * @param {number} nFamily - Number that references the Font family
 * @returns {Font} - Returns the current Font object for chaining
 */
Font.prototype.setFamily = function(nFamily) {

	if (nFamily > 0 && nFamily < 15) {
		this.family.valueAttribute.value = nFamily;
	}

	return this;
};

/**
 * Assigns the given font name to this object.
 *
 * @param {string} sName - Name of the font i.E. Arial, Calibri, etc.
 * @return {Font} - Returns the current Font object for chaining
 */
Font.prototype.setName = function(sName) {
	if (sName && typeof sName === 'string') {
		this.name.valueAttribute.value = sName;
	}

	/*
	 * If the font name is Calibri we need to add another child node:
	 *
	 *	{
	 *		tag: 'scheme',
	 *		attributes: [
	 *			{
	 *				name: 'val',
	 *				value: 'minor'
	 *			}
	 *		]
	 *	}
	 */

	return this;
};

/**
 * Assigns the given font size to this object.
 *
 * @param {number} nSize - Textual representation of the font size
 * @return {Font} - Returns the current Font object for chaining
 */
Font.prototype.setSize = function(nSize) {

	/* Check preconditions */
	if (typeof nSize !== 'number' || isNaN(nSize) || nSize < 1 || nSize > 96) {
		throw new Error('The size ' + nSize + ' is not supported.');
	}

	this.size.valueAttribute.value = nSize;

	return this;
};

module.exports = Font;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/style/Font.js
// module id = 32
// module chunks = 0